/* eslint-disable no-plusplus */
import * as React from 'react';
import styled from '@emotion/styled';

const InputBase = styled.input`
  border: 2px solid #d5dfe9;
  border-radius: 8px;
  width: 54px;
  height: 54px;
  fontsize: 12px;
  color: #000;
  font-weight: 400;
  caret-color: blue;
  margin: 10px;
  text-align: center;

  :focus {
    border: 1px solid #cfd3db;
    outline: none;
  }
`;

const Input: React.FC<any> = (props: any) => {
  const ref = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (props.focus && ref.current) {
      ref.current.focus();
    }
  }, [props.focus]);

  return <InputBase ref={ref} {...props} />;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  hasError?: boolean;
  errorLabel?: string;
};

export const OTP: React.FC<Props> = (props: Props) => {
  const numInputs = 6;

  const [activeInput, setActiveInput] = React.useState<number>(0);
  const inputs: React.ReactElement[] = [];
  const otp = props.value.split('').slice(0, 6) ?? [];

  const setActive = (input: number) => {
    const active = Math.max(Math.min(numInputs - 1, input), 0);
    setActiveInput(active);
  };
  const focusNext = () => setActive(activeInput + 1);
  const focusPrevious = () => setActive(activeInput - 1);

  const callback = () => {
    const v = otp.join('');
    props.onChange(v);
  };

  const setIndex = (idx: number, value: string) => {
    if (idx < numInputs) {
      otp[idx] = value;
    }
    callback();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    let nextActiveInput = activeInput;

    const pastedData = e.clipboardData
      .getData('text/plain')
      .slice(0, numInputs - activeInput)
      .split('');

    for (let pos = 0; pos < numInputs; ++pos) {
      if (pos >= activeInput && pastedData.length > 0) {
        otp[pos] = pastedData.shift() ?? '';
        nextActiveInput += 1;
      }
    }

    callback();
    setActiveInput(nextActiveInput);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setIndex(activeInput, value);
  };

  const onInput = () => {
    focusNext();
  };

  // Handle cases of backspace, delete, left arrow, right arrow, space
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      setIndex(activeInput, '');
      focusPrevious();
    } else if (e.key === 'Delete') {
      e.preventDefault();
      setIndex(activeInput, '');
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setIndex(activeInput, '');
      focusPrevious();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusNext();
    } else if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Space') {
      e.preventDefault();
    }
  };

  const onFocus = (i: number) => (e: React.FocusEvent<HTMLInputElement>) => {
    setActiveInput(i);
    e.target.select();
  };

  for (let i = 0; i < numInputs; i++) {
    inputs.push(
      <Input
        inputmode="numeric"
        autocomplete="one-time-code"
        key={i}
        onPaste={onPaste}
        onChange={onChange}
        value={(otp && otp[i]) ?? ''}
        focus={activeInput === i}
        onInput={onInput}
        onBlur={() => setActiveInput(-1)}
        onKeyDown={onKeyDown}
        onFocus={onFocus(i)}
      />
    );
  }

  return (
    <div>
      {inputs}
      {props.hasError && (
        <p className="text-body font-body text-error-foreground">
          {props.errorLabel ??
            'You have entered an invalid code. Please try again.'}
        </p>
      )}
    </div>
  );
};

export default OTP;
